import React, { useState, useEffect } from 'react'
import { Agent } from '@/types/Agent.types'
import { getTemplateComponent } from '@/components/templates'
import Loading from './Loading'
import { AlertTriangle } from 'lucide-react'
import { useAnalytics } from '@/hooks/useAnalytics'

interface AgentPublicPageProps {
  subdomain: string
}

const AgentPublicPage: React.FC<AgentPublicPageProps> = ({ subdomain }) => {
  const [agent, setAgent] = useState<Agent | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isBlocked, setIsBlocked] = useState(false)

  // Inicializar analytics cuando tengamos el subdomain
  const {
    trackChatOpen,
    trackChatMessage,
    trackDataCaptured,
    trackChatClose,
    trackPropertyView
  } = useAnalytics({
    agentSubdomain: subdomain,
    pageType: 'landing'
  })

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        setLoading(true)
        const response = await fetch(`/api/agents/subdomain/${subdomain}`)
        const data = await response.json()

        // Verificar si el agente está bloqueado
        if (data.blocked) {
          setIsBlocked(true)
          return
        }

        if (!response.ok) {
          setError('Agente no encontrado')
          return
        }

        setAgent(data)
      } catch (err) {
        setError('Agente no encontrado')
        console.error('Error fetching agent:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchAgent()
  }, [subdomain])

  if (loading) return <Loading />

  // Página de sitio bloqueado
  if (isBlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center p-8 max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-500/20 flex items-center justify-center">
            <AlertTriangle className="w-10 h-10 text-amber-500" />
          </div>
          <h2 className="text-2xl font-semibold text-white mb-3">
            Sitio temporalmente no disponible
          </h2>
          <p className="text-slate-400 mb-6">
            Este sitio se encuentra en mantenimiento. Por favor, intenta más tarde.
          </p>
          <a
            href="https://innovagent.tech"
            className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Ir a InnovAgent
          </a>
        </div>
      </div>
    )
  }

  if (error || !agent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
        <div className="text-center p-8">
          <img
            src="https://raw.githubusercontent.com/ruxo-rod/logos/main/logo%20IA.jpg"
            alt="InnovAgent"
            className="h-16 w-16 mx-auto mb-4 rounded-lg opacity-60"
          />
          <h2 className="text-2xl text-gray-600 mb-2">
            {error || 'Agente no encontrado'}
          </h2>
          <p className="text-gray-500">
            Este agente no está disponible en este momento
          </p>
        </div>
      </div>
    )
  }

  // Obtener el componente del template seleccionado
  const TemplateComponent = getTemplateComponent(agent.template || 'elegante')

  // Pasar callbacks de analytics al template
  return (
    <TemplateComponent
      agent={agent}
      analyticsCallbacks={{
        onChatOpen: trackChatOpen,
        onChatMessage: trackChatMessage,
        onDataCaptured: trackDataCaptured,
        onChatClose: trackChatClose,
        onPropertyClick: trackPropertyView
      }}
    />
  )
}

export default AgentPublicPage
